import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { useGeolocation } from "@/hooks/useGeolocation";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LocateFixed, Loader2, MapPin, Store, X } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";

// Fix Leaflet default icon paths (Webpack strips them)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const CHARTRES = { lat: 48.4469, lng: 1.4842 };
const RADII = [
  { v: 5,  l: "- 5 km" },
  { v: 10, l: "- 10 km" },
  { v: 20, l: "- 20 km" },
  { v: 0,  l: "Tout le 28" },
];

function haversineKm(a, b) {
  const r = 6371, p = Math.PI / 180;
  const d = 0.5 - Math.cos((b.lat - a.lat) * p) / 2
          + Math.cos(a.lat * p) * Math.cos(b.lat * p) * (1 - Math.cos((b.lng - a.lng) * p)) / 2;
  return 2 * r * Math.asin(Math.sqrt(d));
}

export default function MapPage() {
  const [points, setPoints] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [radius, setRadius] = useState(10);
  const geo = useGeolocation();
  useSeo({
    title: "La carte des commerces partenaires | PROXIreduc-28",
    description: "Repérez tous les bons plans autour de vous en Eure-et-Loir. Créez votre compte gratuit et profitez des réductions près de chez vous.",
    canonicalPath: "/carte",
    image: "/og-carte.jpg",
  });

  useEffect(() => {
    api.get("/businesses-geo").then((r) => setPoints(r.data || [])).catch(() => setPoints([]));
    api.get("/categories").then((r) => setCategories(r.data || []));
    document.title = "Carte des partenaires | PROXIreduc-28";
  }, []);

  const center = geo.coords || CHARTRES;

  const filtered = useMemo(() => {
    let pts = points.filter((p) => p.lat && p.lng);
    if (cat) pts = pts.filter((p) => (p.category?.slug || p.category_slug) === cat);
    if (geo.coords && radius > 0) {
      pts = pts.filter((p) => haversineKm(geo.coords, { lat: p.lat, lng: p.lng }) <= radius);
    }
    return pts.map((p) => ({
      ...p,
      distance: geo.coords ? Math.round(haversineKm(geo.coords, { lat: p.lat, lng: p.lng }) * 10) / 10 : null,
    }));
  }, [points, cat, radius, geo.coords]);

  return (
    <div data-testid="map-page">
      {/* Header */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-orange-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900">Carte des partenaires</h1>
              <p className="mt-1 text-slate-600 text-sm md:text-base">Explorez les commerces et professionnels partenaires près de chez vous.</p>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Select value={cat} onValueChange={(v) => setCat(v === "all" ? "" : v)}>
                <SelectTrigger className="w-56 h-11 bg-white" data-testid="map-cat"><SelectValue placeholder="Toutes catégories"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {!geo.coords ? (
                <Button onClick={() => geo.request()} className="btn-shine text-white h-11" data-testid="map-geo-ask">
                  {geo.loading ? <Loader2 size={14} className="mr-1 animate-spin"/> : <LocateFixed size={14} className="mr-1"/>}
                  Me localiser
                </Button>
              ) : (
                <Button onClick={() => { geo.clear(); }} variant="outline" className="h-11" data-testid="map-geo-clear">
                  <X size={14} className="mr-1"/> Désactiver la géoloc
                </Button>
              )}
            </div>
          </div>

          {geo.coords && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="map-radius-row">
              <span className="text-xs font-bold text-emerald-800 uppercase tracking-widest flex items-center gap-1"><MapPin size={13}/> Rayon :</span>
              {RADII.map((r) => (
                <button key={r.v} onClick={() => setRadius(r.v)}
                        data-testid={`map-radius-${r.v || "all"}`}
                        className={`px-3 py-1 rounded-full text-xs font-bold border ${radius === r.v ? "bg-emerald-700 text-white border-emerald-700" : "bg-white border-emerald-200 text-emerald-800 hover:border-emerald-500"}`}>
                  {r.l}
                </button>
              ))}
            </div>
          )}
          {geo.error && <div className="mt-2 text-xs text-rose-700">{geo.error}</div>}

          <div className="mt-3 text-sm text-slate-500" data-testid="map-count">
            {filtered.length} partenaire{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""}
          </div>
        </div>
      </section>

      {/* Carte */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm" style={{ height: "72vh", minHeight: 480 }}>
          <MapContainer center={[center.lat, center.lng]} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom data-testid="map-container">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {geo.coords && (
              <>
                <CircleMarker center={[geo.coords.lat, geo.coords.lng]} radius={8} pathOptions={{ color: "#10b981", fillColor: "#10b981", fillOpacity: 0.9 }}>
                  <Popup>Vous êtes ici.</Popup>
                </CircleMarker>
                {radius > 0 && (
                  <Circle center={[geo.coords.lat, geo.coords.lng]} radius={radius * 1000}
                          pathOptions={{ color: "#10b981", fillColor: "#10b981", fillOpacity: 0.08 }}/>
                )}
              </>
            )}
            {filtered.map((p) => (
              <Marker key={p.id} position={[p.lat, p.lng]}>
                <Popup>
                  <div className="min-w-[200px]">
                    <div className="font-bold text-slate-900">{p.name}</div>
                    {p.category?.name && <div className="text-xs text-slate-500">{p.category.name}</div>}
                    {p.city && <div className="text-xs text-slate-600 mt-1 flex items-center gap-1"><MapPin size={11}/> {p.city}</div>}
                    {p.distance != null && (
                      <div className="text-xs text-emerald-700 font-semibold mt-1">À {p.distance} km de vous</div>
                    )}
                    {p.offer_count > 0 && (
                      <div className="mt-1 text-xs text-[#E05638] font-semibold">{p.offer_count} offre{p.offer_count > 1 ? "s" : ""} active{p.offer_count > 1 ? "s" : ""}</div>
                    )}
                    <Link to={`/commerce/${p.id}`} className="mt-2 inline-block text-sm text-[#E05638] font-semibold hover:underline" data-testid={`map-popup-link-${p.id}`}>
                      Voir le partenaire →
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        {filtered.length === 0 && (
          <div className="mt-6 text-center text-slate-500 text-sm" data-testid="map-empty">
            <Store size={32} className="mx-auto text-slate-300 mb-2"/>
            Aucun partenaire géolocalisé dans ce périmètre.
          </div>
        )}
      </section>
    </div>
  );
}
